import { useCallback } from 'react';
import { useSnackbar } from '../providers/SnackbarProvider';

/**
 * Unified notification hook backed by SnackbarProvider.
 */
export const useNotifications = () => {
  const { showSuccess, showError, showInfo, showWarning } = useSnackbar();

  const notify = useCallback(
    (message: string, variant: string = 'default') => {
      if (variant === 'error') showError(message);
      else if (variant === 'success') showSuccess(message);
      else if (variant === 'warning') showWarning(message);
      else showInfo(message);
      return message;
    },
    [showSuccess, showError, showInfo, showWarning]
  );

  const success = useCallback(
    (message: string) => {
      showSuccess(message);
      return message;
    },
    [showSuccess]
  );

  const error = useCallback(
    (message: string) => {
      showError(message);
      return message;
    },
    [showError]
  );

  const warning = useCallback(
    (message: string) => {
      showWarning(message);
      return message;
    },
    [showWarning]
  );

  const info = useCallback(
    (message: string) => {
      showInfo(message);
      return message;
    },
    [showInfo]
  );

  const promise = useCallback(
    async <T>(
      promiseToResolve: Promise<T>,
      messages: { loading: string; success: string; error: string | ((error: unknown) => string) }
    ): Promise<T> => {
      try {
        const result = await promiseToResolve;
        showSuccess(messages.success);
        return result;
      } catch (err) {
        const errorMessage =
          typeof messages.error === 'function' ? messages.error(err) : messages.error;
        showError(errorMessage);
        throw err;
      }
    },
    [showSuccess, showError]
  );

  const close = useCallback(() => {}, []);

  return { notify, success, error, warning, info, promise, close };
};
